import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSpreadsheet } from "lucide-react";
import api, { downloadExcel } from "../api";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { fmtMoney } from "../utils/format";

export default function Rentabilite() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/rentabilite").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  const totals = rows.reduce((acc, r) => ({
    cost: acc.cost + (r.cost_basis || 0),
    profit: acc.profit + (r.profit || 0),
    sale: acc.sale + (r.sale_price || 0),
  }), { cost: 0, profit: 0, sale: 0 });

  return (
    <div className="space-y-5" data-testid="rentabilite-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Rentabilité par véhicule</h1>
          <p className="mt-1 text-sm text-slate-500">
            Bénéfice total réalisé : <span className="font-mono-num font-bold text-emerald-600">{fmtMoney(totals.profit)}</span>
            {" "}· ROI global : <span className="font-mono-num font-bold text-cyan-700">{totals.cost ? ((totals.profit / totals.cost) * 100).toFixed(2) : 0} %</span>
          </p>
        </div>
        <Button variant="outline" onClick={() => downloadExcel("/export/excel?type=rentabilite")} data-testid="export-rentabilite-excel">
          <FileSpreadsheet className="mr-1 h-4 w-4" /> Export Excel
        </Button>
      </div>

      <DataTable testidPrefix="rentabilite-table" rows={rows}
        searchKeys={["internal_number", "label"]}
        onRowClick={(r) => navigate(`/vehicules/${r.id}`)}
        columns={[
          { key: "internal_number", label: "Véhicule", render: (r) => (
            <div><p className="font-semibold text-slate-800">{r.label}</p><p className="font-mono-num text-xs text-slate-400">{r.internal_number}</p></div>
          ) },
          { key: "status", label: "Statut", render: (r) => <StatusBadge status={r.status} /> },
          { key: "purchase_price_fcfa", label: "Achat", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.purchase_price_fcfa)}</span> },
          { key: "frais_import", label: "Import", className: "text-right", render: (r) => <span className="font-mono-num text-xs">{fmtMoney(r.frais_import)}</span> },
          { key: "port", label: "Port", className: "text-right", render: (r) => <span className="font-mono-num text-xs">{fmtMoney(r.port)}</span> },
          { key: "douane", label: "Douane", className: "text-right", render: (r) => <span className="font-mono-num text-xs">{fmtMoney(r.douane)}</span> },
          { key: "entretien", label: "Entretien", className: "text-right", render: (r) => <span className="font-mono-num text-xs">{fmtMoney(r.entretien + (r.carburant || 0))}</span> },
          { key: "autres", label: "Autres", className: "text-right", render: (r) => <span className="font-mono-num text-xs">{fmtMoney(r.autres)}</span> },
          { key: "cost_basis", label: "Coût total", className: "text-right", render: (r) => <span className="font-mono-num font-bold">{fmtMoney(r.cost_basis)}</span> },
          { key: "sale_price", label: "Vente", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.sale_price ?? r.target_sale_price)}</span> },
          { key: "profit", label: "Bénéfice", className: "text-right", render: (r) =>
            r.profit != null ? <span className={`font-mono-num font-bold ${r.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(r.profit)}</span> : <span className="text-slate-300">—</span> },
          { key: "margin_pct", label: "Marge %", className: "text-right", render: (r) => r.margin_pct != null ? <span className="font-mono-num font-semibold text-cyan-700">{r.margin_pct} %</span> : "—" },
          { key: "roi", label: "ROI", className: "text-right", render: (r) => r.roi != null ? <span className="font-mono-num font-semibold">{r.roi} %</span> : "—" },
        ]}
      />
    </div>
  );
}
